import React from "react";
import { useTranslation } from "react-i18next";
import "../styles/components/WeatherDayModal.css";

// --- ROBUST ICON HELPER (Fixes the Question Mark) ---
const getWeatherIcon = (day) => {
  // 1. Try string name (New Backend)
  const iconName = day.icon;
  const map = {
    "clear": "☀️",
    "partly-cloudy": "⛅",
    "cloudy": "☁️",
    "rain": "🌧️",
    "showers": "🌦️",
    "storm": "⛈️",
    "snow": "❄️",
    "fog": "🌫️",
    "drizzle": "💧"
  };
  if (map[iconName]) return map[iconName];

  // 2. Fallback: Numeric Codes (Old/Cached Data)
  const code = day.weatherCode;
  if (code === 0) return "☀️";
  if (code > 0 && code < 40) return "⛅";
  if (code >= 40 && code < 50) return "🌫️";
  if (code >= 50 && code < 60) return "💧";
  if (code >= 60 && code < 70) return "🌧️";
  if (code >= 70 && code < 80) return "❄️";
  if (code >= 80 && code < 85) return "🌦️";
  if (code >= 95) return "⛈️";

  return "❓";
};

// --- MOON ICON HELPER ---
const getMoonIcon = (phaseName) => {
  if (!phaseName) return "";
  const p = phaseName.toLowerCase();
  if (p.includes("new")) return "🌑";
  if (p.includes("crescent")) return "🌙";
  if (p.includes("quarter")) return "🌓";
  if (p.includes("gibbous")) return "🌔";
  if (p.includes("full")) return "🌕";
  return "🌑";
};

// ... imports and helpers stay the same ...

export default function WeatherDayModal({ day, onClose, isPastTripView = false }) {
  const { t } = useTranslation();

  if (!day) return null;

  const dateObj = new Date(day.date);
  const dateStr = dateObj.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="weather-modal-overlay" onClick={onClose}>
      <div className="weather-modal-content" onClick={(e) => e.stopPropagation()}>
        
        <button className="weather-modal-close" onClick={onClose}>✖</button>

        <h3 className="weather-modal-date">{dateStr}</h3>
        
        <div className="weather-modal-main-icon">
          {getWeatherIcon(day)}
        </div>
        
        <p className="weather-modal-desc">
            {day.description || t("weather_unknown")}
        </p>

        <div className="weather-modal-grid">
          
          {/* High Temp (Red Thermometer) */}
          <div className="weather-modal-item">
            <span className="weather-modal-label">🌡️ {t("weather_high")}</span>
            <span className="weather-modal-value high">
              {Math.round(day.tempMax)}°C
            </span>
          </div>

          {/* Low Temp (Blue Thermometer via CSS) */}
          <div className="weather-modal-item">
            <span className="weather-modal-label">
                {/* ❄️ replaced with Blue Thermometer */}
                <span className="thermometer-blue">🌡️</span> {t("weather_low")}
            </span>
            <span className="weather-modal-value low">
              {Math.round(day.tempMin)}°C
            </span>
          </div>

          {/* Wind */}
          <div className="weather-modal-item">
            <span className="weather-modal-label">💨 {t("weather_wind")}</span>
            <div className="weather-modal-value">
              {Math.round(day.windSpeed)} km/h
              <div className="weather-modal-subtext">{day.windDir || t("weather_wind_variable_short")}</div>
            </div>
          </div>

          {/* Water */}
          {day.waterTemp && (
            <div className="weather-modal-item">
              <span className="weather-modal-label">💧 {t("weather_water")}</span>
              <span className="weather-modal-value water">
                {Math.round(day.waterTemp)}°C
              </span>
            </div>
          )}

          {/* Sunrise */}
          {day.sunrise && (
             <div className="weather-modal-item">
               <span className="weather-modal-label">🌅 {t("weather_sunrise")}</span>
               <span className="weather-modal-value">{day.sunrise}</span>
             </div>
          )}

          {/* Sunset */}
          {day.sunset && (
             <div className="weather-modal-item">
               <span className="weather-modal-label">🌇 {t("weather_sunset")}</span>
               <span className="weather-modal-value">{day.sunset}</span>
             </div>
          )}
        </div>
        
        {/* MOON PHASE SECTION (UPDATED) */}
        {day.moon && (
            <div className="weather-modal-moon-section">
                <div className="weather-modal-moon-icon">
                    {getMoonIcon(day.moon.phase)}
                </div>
                <div className="weather-modal-moon-text">
                    <span className="weather-modal-moon-phase">
                        {/* Phase Name + Illumination % */}
                        {day.moon.phase} ({day.moon.illumination}%)
                    </span>
                    <span className="weather-modal-moon-age">
                        {t("moon_age")}: {t("weather_moon_age_days", {
                          defaultValue: "{{count}} days",
                          count: day.moon.age_days,
                        })}
                    </span>
                </div>
            </div>
        )}

        <div className="weather-modal-footer">
           {day.isForecast ? (
               <span className="badge-live">📡 {t("weather_live_forecast")}</span>
           ) : (
               <span className="badge-avg">📊 {isPastTripView ? t("weather_historical_conditions", { defaultValue: "Historical Conditions" }) : t("weather_historical_avg", { defaultValue: "Historical Average" })}</span>
           )}
        </div>

      </div>
    </div>
  );
}
